import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { ThemeContext } from '../context/ThemeContext';
import { API_BASE } from '../api';

function UnassignedTasks() {
  const [tasks, setTasks] = useState([]);
  const [editors, setEditors] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [assigningDesigner, setAssigningDesigner] = useState(false);
  const [message, setMessage] = useState('');
  const { currentTheme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const ASSIGN_API_ENDPOINT = `${API_BASE}/assign_task.php`;

  // 🎨 GLOBAL COLORS DEFINITION
  const colors = {
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',

    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',

    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
    gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
    gradient4: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' 
  };

  // ✅ VIDEO TYPE CONFIGURATION
  const videoTypes = [
    { value: 'indoor_reel', label: 'Indoor Reel', color: '#6366F1', icon: 'fa-home' },
    { value: 'outdoor_reel', label: 'Outdoor Reel', color: '#10B981', icon: 'fa-tree' },
    { value: 'podcast', label: 'Podcast', color: '#8B5CF6', icon: 'fa-podcast' },
    { value: 'indoor_long_video', label: 'Indoor Long', color: '#F59E0B', icon: 'fa-video' },
    { value: 'outdoor_long_video', label: 'Outdoor Long', color: '#06B6D4', icon: 'fa-mountain' },
  ];

  // ✅ VIDEO TYPE BADGE COMPONENT
  const getVideoTypeBadge = (videoType) => {
    if (!videoType || videoType === 'NULL' || videoType === 'null' || !videoTypes.find(type => type.value === videoType)) {
      return (
        <span 
          className="badge" 
          style={{
            background: '#6B7280',
            color: 'white',
            fontSize: '0.65rem',
            padding: '4px 8px',
            borderRadius: '6px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            minWidth: '70px',
            justifyContent: 'center',
            border: '1px solid rgba(255,255,255,0.2)'
          }}
          title="Video type not specified"
        >
          N/A
        </span>
      );
    }

    const typeConfig = videoTypes.find(type => type.value === videoType);
    
    return (
      <span 
        className="badge" 
        style={{
          background: typeConfig.color,
          color: 'white',
          fontSize: '0.65rem',
          padding: '4px 8px',
          borderRadius: '6px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          minWidth: '70px',
          justifyContent: 'center',
          border: '1px solid rgba(255,255,255,0.2)'
        }}
        title={typeConfig.label}
      >
        <i className={`fas ${typeConfig.icon}`} style={{fontSize: '0.5rem'}}></i>
        {typeConfig.label}
      </span>
    );
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    if (i === 0) return `${bytes} ${sizes[i]}`;
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const Tooltip = ({ text, children, maxWidth = '300px' }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    return (
      <div 
        style={{ position: 'relative', display: 'inline-block', width: '100%' }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {children}
        {showTooltip && text && (
          <div style={{
            visibility: 'visible',
            width: maxWidth,
            backgroundColor: '#1a1a2e',
            color: 'white',
            textAlign: 'center',
            borderRadius: '8px',
            padding: '10px',
            position: 'absolute',
            zIndex: 1000,
            bottom: '125%',
            left: '50%',
            transform: 'translateX(-50%)',
            opacity: 1,
            transition: 'opacity 0.3s',
            fontSize: '0.75rem',
            border: `1px solid ${colors.borderDark}`,
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {text}
          </div>
        )}
      </div>
    );
  };

  const truncateText = (text, maxLength = 15) => {
    if (!text || text === '-' || text === 'NULL' || text === 'null') return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  useEffect(() => {
    fetchTasks();
    fetchEditors();
    fetchDesigners();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/get_tasks.php`);
      const data = await response.json();
      let tasksArray = Array.isArray(data) ? data : (data.data || []);
      const unassignedTasks = tasksArray.filter(task => {
        const isEditorAssigned = task.editor_id && task.editor_id !== '' && task.editor_id !== 'NULL';
        const isDesignerAssigned = task.designer_id && task.designer_id !== '' && task.designer_id !== 'NULL';
        return !isEditorAssigned || !isDesignerAssigned;
      });
      setTasks(unassignedTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEditors = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_editors.php`);
      const data = await response.json();
      setEditors(Array.isArray(data) ? data : (data.data || []));
    } catch (error) { console.error(error); }
  };

  const fetchDesigners = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_designers.php`);
      const data = await response.json();
      setDesigners(data.success ? data.data : (Array.isArray(data) ? data : []));
    } catch (error) { console.error(error); }
  };

  const assignTaskToEditor = async (taskId, editorId) => {
    if (!editorId) return;
    try {
      setAssigning(true);
      const editor = editors.find(ed => ed.id == editorId);
      const response = await fetch(ASSIGN_API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operation: 'assign_editor',
          task_id: parseInt(taskId),
          editor_id: parseInt(editorId),
          editor_name: editor?.name || '',
          assigned_by: parseInt(sessionStorage.getItem('userId') || 1),
          assigned_by_name: sessionStorage.getItem('userName') || 'Admin'
        })
      });
      const data = await response.json();
      if (data.success) fetchTasks();
    } catch (error) { console.error(error); } finally { setAssigning(false); }
  };

  const assignTaskToDesigner = async (taskId, designerId) => {
    if (!designerId) return;
    try {
      setAssigningDesigner(true);
      const designer = designers.find(d => d.id == designerId);
      const response = await fetch(ASSIGN_API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operation: 'assign_designer',
          task_id: parseInt(taskId),
          designer_id: parseInt(designerId),
          designer_name: designer?.name || '',
          assigned_by: parseInt(sessionStorage.getItem('userId') || 1),
          assigned_by_name: sessionStorage.getItem('userName') || 'Admin'
        })
      });
      const data = await response.json();
      if (data.success) fetchTasks();
    } catch (error) { console.error(error); } finally { setAssigningDesigner(false); }
  };

  const handleOpenLinkClick = (task) => {
    const rawLink = task.raw_link || task.raw_drive_link || task.raw_drive;
    if (rawLink) window.open(rawLink, '_blank', 'noopener,noreferrer');
  };

  const handleVideoNameClick = (task) => navigate('/task-full-details', { state: { task } });
  const handleClientClick = (clientId, clientName) => navigate('/client-tasks', { state: { clientId, clientName } });
  const handleOperatorClick = (operatorId, operatorName) => navigate('/operator-tasks', { state: { operatorId, operatorName } });

  const isEditorAssigned = (task) => task.editor_id && task.editor_id !== '' && task.editor_id !== 'NULL';
  const isDesignerAssigned = (task) => task.designer_id && task.designer_id !== '' && task.designer_id !== 'NULL';

  const getStatusBadge = (task) => {
    const statusConfig = {
      pending: { class: 'bg-warning text-dark', text: 'Pending', icon: 'fa-clock' },
      in_progress: { class: 'bg-info text-white', text: 'In Progress', icon: 'fa-spinner' },
      completed: { class: 'bg-success text-white', text: 'Completed', icon: 'fa-check' },
      assigned: { class: 'bg-primary text-white', text: 'Assigned', icon: 'fa-user-check' },
    };
    const config = statusConfig[task.status] || statusConfig.pending;
    return <span className={`badge ${config.class}`} style={{fontSize: '0.7rem'}}><i className={`fas ${config.icon} me-1`}></i>{config.text}</span>;
  };

  const getEditorStatusBadge = (task) => {
    if (isEditorAssigned(task)) return <span className="badge bg-success" style={{fontSize: '0.7rem'}}>{truncateText(task.editor_name, 8)}</span>;
    return <span className="badge bg-secondary" style={{fontSize: '0.7rem'}}>Unassigned</span>;
  };

  const getDesignerStatusBadge = (task) => {
    if (isDesignerAssigned(task)) return <span className="badge bg-success" style={{fontSize: '0.7rem'}}>{truncateText(task.designer_name, 8)}</span>;
    return <span className="badge bg-secondary" style={{fontSize: '0.7rem'}}>Unassigned</span>;
  };

  const getDropdownStyle = (isAssigned) => ({
    background: isAssigned ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.1)',
    border: `2px solid ${isAssigned ? colors.success : colors.borderDark}`,
    borderRadius: '6px',
    padding: '6px 10px',
    color: isAssigned ? colors.success : 'white',
    fontSize: '0.75rem',
    width: '100%',
    cursor: 'pointer'
  });

  const stylesInternal = {
    container: { minHeight: '100vh', background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`, position: 'relative' },
    header: { padding: '30px 30px 20px', position: 'relative', zIndex: 2 },
    headerContent: { maxWidth: '1400px', margin: '0 auto' },
    pageTitle: { fontSize: '2.5rem', fontWeight: '800', background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0 },
    content: { padding: '0 30px 30px', maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 2 },
    formCard: { background: 'rgba(255, 255, 255, 0.05)', borderRadius: '20px', border: `1px solid ${colors.borderDark}`, overflow: 'hidden', boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)' },
    cardHeader: { background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, color: 'white', padding: '20px 25px' },
    tableHeader: { background: 'rgba(255, 255, 255, 0.1)', color: 'white', borderBottom: `2px solid ${colors.borderDark}`, padding: '12px 8px', fontWeight: '600', fontSize: '0.85rem' },
    tableRow: { borderBottom: '1px solid rgba(255, 255, 255, 0.05)', color: 'white' },
    tableCell: { padding: '10px 8px', verticalAlign: 'middle', fontSize: '0.8rem' }
  };

  if (loading) return <Layout><div style={stylesInternal.container}><div style={{color: 'white', textAlign: 'center', padding: '100px'}}>Loading...</div></div></Layout>;

  return (
    <Layout>
      <div style={stylesInternal.container}>
        <div style={stylesInternal.header}>
          <div style={stylesInternal.headerContent}>
            <h1 style={stylesInternal.pageTitle}>Unassigned Tasks</h1>
          </div>
        </div>

        <div style={stylesInternal.content}>
          <div style={stylesInternal.formCard}>
            <div style={stylesInternal.cardHeader}><h3 style={{margin: 0, fontSize: '1.5rem'}}>Overview</h3></div>
            <div style={{overflowX: 'auto'}}>
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead>
                  <tr>
                    <th style={stylesInternal.tableHeader}>#</th>
                    <th style={stylesInternal.tableHeader}>Video</th>
                    <th style={stylesInternal.tableHeader}>Type</th>
                    <th style={stylesInternal.tableHeader}>Client</th>
                    <th style={stylesInternal.tableHeader}>Status</th>
                    <th style={stylesInternal.tableHeader}>Editor</th>
                    <th style={stylesInternal.tableHeader}>Designer</th>
                    <th style={stylesInternal.tableHeader}>Raw</th>
                    <th style={stylesInternal.tableHeader}>Size</th>
                    <th style={stylesInternal.tableHeader}>Date</th>
                    <th style={stylesInternal.tableHeader}>Operator</th>
                    <th style={stylesInternal.tableHeader}>Notes</th>
                    <th style={stylesInternal.tableHeader}>Assign Editor</th>
                    <th style={stylesInternal.tableHeader}>Assign Designer</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task, index) => (
                    <tr key={task.id} style={stylesInternal.tableRow}>
                      {/* 1. # Index - WHITE */}
                      <td style={stylesInternal.tableCell}>
                        <span style={{ color: '#FFFFFF', fontWeight: 'bold' }}>{index + 1}</span>
                      </td>

                      {/* 2. Video - WHITE, NO ICON */}
                      <td style={stylesInternal.tableCell}>
                        <Tooltip text={task.video_name}>
                          <button onClick={() => handleVideoNameClick(task)} style={{ color: '#FFFFFF', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', textAlign: 'left', padding: 0 }}>
                            {truncateText(task.video_name, 12)}
                          </button>
                        </Tooltip>
                      </td>

                      <td style={stylesInternal.tableCell}>{getVideoTypeBadge(task.video_type)}</td>

                      {/* 3. Client - WHITE, NO ICON */}
                      <td style={stylesInternal.tableCell}>
                        <Tooltip text={task.client_name}>
                          <button onClick={() => handleClientClick(task.client_id, task.client_name)} style={{ color: '#FFFFFF', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', padding: 0 }}>
                            {truncateText(task.client_name, 10)}
                          </button>
                        </Tooltip>
                      </td>

                      <td style={stylesInternal.tableCell}>{getStatusBadge(task)}</td>
                      <td style={stylesInternal.tableCell}>{getEditorStatusBadge(task)}</td>
                      <td style={stylesInternal.tableCell}>{getDesignerStatusBadge(task)}</td>
                      
                      <td style={stylesInternal.tableCell}>
                        <button onClick={() => handleOpenLinkClick(task)} style={{ background: colors.primary, color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem' }}>View</button>
                      </td>

                      <td style={stylesInternal.tableCell}><span style={{color: colors.textLight}}>{formatFileSize(task.file_size || task.size)}</span></td>
                      
                      <td style={stylesInternal.tableCell}><small style={{color: colors.textLight}}>{task.shoot_date || 'N/A'}</small></td>

                      {/* 4. Operator - WHITE, NO ICON */}
                      <td style={stylesInternal.tableCell}>
                        <Tooltip text={task.operator_name}>
                          <button onClick={() => handleOperatorClick(task.operator_id, task.operator_name)} style={{ color: '#FFFFFF', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', padding: 0 }}>
                            {truncateText(task.operator_name, 8)}
                          </button>
                        </Tooltip>
                      </td>

                      {/* 5. Notes - WHITE */}
                      <td style={stylesInternal.tableCell}>
                        <Tooltip text={task.editor_notes || task.notes}>
                          <span style={{ color: '#FFFFFF' }}>{truncateText(task.editor_notes || task.notes, 15)}</span>
                        </Tooltip>
                      </td>

                      <td style={stylesInternal.tableCell}>
                        <select style={getDropdownStyle(isEditorAssigned(task))} value={task.editor_id || ""} onChange={(e) => assignTaskToEditor(task.id, e.target.value)}>
                          <option value="">Select Editor</option>
                          {editors.map(e => <option key={e.id} value={e.id} style={{background: '#0f0c29'}}>{e.name}</option>)}
                        </select>
                      </td>

                      <td style={stylesInternal.tableCell}>
                        <select style={getDropdownStyle(isDesignerAssigned(task))} value={task.designer_id || ""} onChange={(e) => assignTaskToDesigner(task.id, e.target.value)}>
                          <option value="">Select Designer</option>
                          {designers.map(d => <option key={d.id} value={d.id} style={{background: '#0f0c29'}}>{d.name}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default UnassignedTasks;